/**
 * SchemaDiscoverySheet — browsable catalog of every entity + measure
 * the AI Report Builder's semantic layer exposes.
 *
 * Why this exists
 * ───────────────
 * The semantic schema went from 9 to 21 entities (Feb 2026 expansion).
 * That's a great problem to have, but it also means a user typing
 * "what can I ask?" has no way to discover the surface area without
 * trial-and-error. This sheet flips that around — open from the
 * builder hero ("Schema" button), browse every entity, search by
 * keyword, and click any measure to drop a ready-to-send prompt
 * into the input box ("Show me <measure> grouped by <first
 * dimension>").
 *
 * Data source: GET /api/mc/reports/catalog (returns entities[] with
 * id/label/description/dimensions/measures/time_field). Loaded once
 * by the parent and passed in as props.
 *
 * UX rules
 * ────────
 * • Right-side slide-over (doesn't blow up the builder canvas).
 * • Sticky search bar filters across entity label / description /
 *   measure label / dimension name — typing "motivation" instantly
 *   narrows to the one entity that matters.
 * • Each entity card lists dimensions (chips) + measures (clickable
 *   rows). Clicking a measure closes the sheet and pushes a
 *   suggested prompt to the parent — the parent then focuses the
 *   input so the user can edit before sending.
 */
import React, { useMemo, useState } from 'react';
import { Search, Database, X, ArrowRight, Clock, Tag } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';

const AGG_LABEL = {
  count: 'count',
  count_distinct: 'distinct',
  sum: 'sum',
  avg: 'avg',
  min: 'min',
  max: 'max',
  latest: 'latest',
  first: 'first',
  count_if: 'conditional count',
  ratio_if: 'ratio',
};

function entityMatches(entity, q) {
  if (!q) return true;
  const needle = q.toLowerCase();
  const hay = [
    entity.label,
    entity.id,
    entity.description,
    ...(entity.dimensions || []),
    ...((entity.measures || []).map((m) => `${m.id} ${m.label || ''}`)),
  ]
    .join(' ')
    .toLowerCase();
  return hay.includes(needle);
}

/**
 * Build a sensible default prompt from an entity + measure pick.
 * The user can edit before sending, so we lean toward concrete +
 * actionable phrasing rather than safe-but-generic.
 */
function suggestPromptFor(entity, measure) {
  const label = (measure.label || measure.id || 'value').toLowerCase();
  const dims = entity.dimensions || [];
  // Prefer the most "useful" grouping dim if available.
  const grouping = dims.find((d) =>
    [
      'organization_id',
      'pillar_name',
      'pillar',
      'measure_id',
      'cohort_id',
      'department',
      'maturity_band',
      'kpi_id',
      'status',
      'role',
    ].includes(d),
  );
  if (grouping) {
    return `Show me ${label} grouped by ${grouping.replace(/_/g, ' ')}`;
  }
  if (entity.time_field) {
    return `Show me ${label} over the last 90 days`;
  }
  return `Show me ${label} across ${entity.label.toLowerCase()}`;
}

export default function SchemaDiscoverySheet({
  open,
  onOpenChange,
  entities = [],
  onPickPrompt,
}) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(
    () => (entities || []).filter((e) => entityMatches(e, query)),
    [entities, query],
  );

  const handleMeasureClick = (entity, measure) => {
    const prompt = suggestPromptFor(entity, measure);
    onPickPrompt?.(prompt);
    onOpenChange?.(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto p-0"
        data-testid="schema-discovery-sheet"
      >
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 p-5 space-y-3">
          <SheetHeader className="space-y-1">
            <SheetTitle className="flex items-center gap-2 text-slate-900">
              <div className="rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 p-1.5">
                <Database className="h-4 w-4 text-white" />
              </div>
              What can I ask?
            </SheetTitle>
            <SheetDescription className="text-xs text-slate-500">
              Every entity + measure the AI Report Builder understands.
              Click any measure to drop a ready-to-send prompt into the
              chat — edit before sending.
            </SheetDescription>
          </SheetHeader>
          {/* Search */}
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search entities, dimensions, or measures…"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              data-testid="schema-discovery-search"
            />
            {query ? (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
                data-testid="schema-discovery-clear"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
          <div className="text-[11px] text-slate-500">
            {filtered.length} of {entities.length} entit{entities.length === 1 ? 'y' : 'ies'}
          </div>
        </div>

        <div className="p-5 space-y-4" data-testid="schema-discovery-list">
          {filtered.length === 0 ? (
            <div className="text-sm text-slate-500 italic text-center py-12">
              No entities match “{query}”. Try a different keyword.
            </div>
          ) : null}

          {filtered.map((entity) => (
            <article
              key={entity.id}
              className="rounded-xl border border-slate-200 bg-white overflow-hidden"
              data-testid={`schema-entity-${entity.id}`}
            >
              {/* Entity header */}
              <header className="p-4 bg-slate-50/60 border-b border-slate-200">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-slate-900">
                      {entity.label}
                    </h3>
                    <code className="text-[10px] text-slate-500 font-mono">
                      {entity.id}
                    </code>
                  </div>
                  {entity.time_field ? (
                    <span className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] text-slate-500 bg-white border border-slate-200 rounded px-1.5 py-0.5">
                      <Clock className="h-2.5 w-2.5" />
                      {entity.time_field}
                    </span>
                  ) : null}
                </div>
                {entity.description ? (
                  <p className="text-xs text-slate-600 mt-2 leading-snug">
                    {entity.description}
                  </p>
                ) : null}
              </header>

              {/* Dimensions */}
              {entity.dimensions?.length ? (
                <div className="px-4 py-3 border-b border-slate-100">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Tag className="h-3 w-3 text-slate-400" />
                    <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">
                      Group by
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {entity.dimensions.map((d) => (
                      <span
                        key={d}
                        className="text-[10px] font-mono text-slate-700 bg-slate-100 rounded px-1.5 py-0.5"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Measures */}
              {entity.measures?.length ? (
                <div className="px-4 py-3">
                  <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1.5">
                    Measures — click to ask
                  </div>
                  <ul className="space-y-1">
                    {entity.measures.map((m) => (
                      <li key={m.id}>
                        <button
                          type="button"
                          onClick={() => handleMeasureClick(entity, m)}
                          className="w-full text-left flex items-center justify-between gap-3 rounded-lg px-2.5 py-1.5 hover:bg-indigo-50 hover:text-indigo-900 transition-colors group"
                          data-testid={`schema-measure-${entity.id}-${m.id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-medium text-slate-800 group-hover:text-indigo-900 truncate">
                              {m.label || m.id}
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono truncate">
                              {m.id} · {AGG_LABEL[m.agg] || m.agg}
                            </div>
                          </div>
                          <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-indigo-600 flex-shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
